import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { useToast } from '../hooks/use-toast';
import FacebookPixelService from '../services/facebookPixel';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send message');
      }

      const result = await response.json();
      
      if (result.success) {
        // Track Facebook Pixel - Lead event
        FacebookPixelService.trackLead({
          content_name: 'Contact Form',
          content_category: 'Lead'
        });

        toast({
          title: "Messaggio inviato!",
          description: "Grazie per averci contattato. Ti risponderemo entro 24 ore.",
        });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        variant: "destructive",
        title: "Errore invio",
        description: error.message || "Si è verificato un errore durante l'invio del messaggio. Riprova più tardi.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Contattaci
          </h1>
          <p className="text-xl max-w-3xl mx-auto">
            Siamo qui per aiutarti. Contatta Lacinio SRL per qualsiasi domanda sui nostri amari personalizzati.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Informazioni di Contatto</h2>
              
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 text-purple-600 rounded-lg">
                      <Mail className="w-6 h-6" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Email</h3>
                    <p className="text-gray-600 mb-2">Per domande generali e supporto clienti</p>
                    <a href="mailto:info@iltuoamaro.it" className="text-purple-600 hover:text-purple-700 font-medium">
                      info@iltuoamaro.it
                    </a>
                    <p className="text-gray-600 mb-2 mt-3">Per preventivi personalizzati</p>
                    <a href="mailto:preventivi@iltuoamaro.it" className="text-purple-600 hover:text-purple-700 font-medium">
                      preventivi@iltuoamaro.it
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 text-purple-600 rounded-lg">
                      <Phone className="w-6 h-6" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">WhatsApp</h3>
                    <p className="text-gray-600 mb-2">Assistenza rapida via WhatsApp</p>
                    <a href="https://wa.me/393939587340" className="text-purple-600 hover:text-purple-700 font-medium">
                      +39 393 9587340
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 text-purple-600 rounded-lg">
                      <MapPin className="w-6 h-6" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Indirizzo</h3>
                    <p className="text-gray-600">
                      <strong>Lacinio SRL</strong><br />
                      Via G. Mercalli, 16 Z.I.<br />
                      88900 Crotone (KR)<br />
                      P.IVA: 03714460791<br />
                      REA: KR - 184420<br />
                      Licenza Doganale: IT00KRX00033F
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 text-purple-600 rounded-lg">
                      <Clock className="w-6 h-6" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Orari di Assistenza</h3>
                    <div className="text-gray-600 space-y-1">
                      <p>Lunedi - Venerdì: 9:00 - 18:00</p>
                      <p>Sabato: 9:00 - 13:00</p>
                      <p>Domenica: Chiuso</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Additional Information */}
              <div className="mt-12 p-6 bg-purple-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Tempi di Risposta</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• <strong>Email:</strong> Risposta entro 24 ore</li>
                  <li>• <strong>Telefono:</strong> Risposta immediata negli orari di assistenza</li>
                  <li>• <strong>Chat:</strong> Disponibile dal Lun-Ven 9:00-18:00</li>
                </ul>
              </div>
            </div>
            
            {/* Contact Form */}
            <div>
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Invia un Messaggio</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Nome Completo *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        placeholder="Il tuo nome"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        placeholder="la-tua-email@esempio.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefono/Cellulare *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      placeholder="+39 xxx xxx xxxx"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      Oggetto *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                    >
                      <option value="">Seleziona un oggetto</option>
                      <option value="informazioni-prodotto">Informazioni sui prodotti</option>
                      <option value="supporto-ordine">Supporto ordine</option>
                      <option value="collaborazioni">Collaborazioni aziendali</option>
                      <option value="eventi-matrimoni">Eventi e matrimoni</option>
                      <option value="reclami">Reclami</option>
                      <option value="altro">Altro</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Messaggio *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors resize-vertical"
                      placeholder="Scrivi il tuo messaggio qui..."
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full flex items-center justify-center px-6 py-4 rounded-lg font-semibold transition-colors duration-200 ${
                      isSubmitting
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                        Invio in corso...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-3" />
                        Invia Messaggio
                      </>
                    )}
                  </button>
                </form>
                
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>Privacy:</strong> I tuoi dati personali saranno utilizzati esclusivamente per rispondere alla tua richiesta e non saranno condivisi con terze parti.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Contact;